import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BrandLogo, COMPANY_NAME, SOFTWARE_NAME } from "@/lib/brand";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-sm text-slate-500">Loading...</div></div>;
  }
  if (user) {
    let target = "/dashboard";
    if (user.role === "technician") target = "/technician/dashboard";
    else if (user.role === "spares") target = "/spares/dashboard";
    else if (user.role === "sales") target = "/reports/dealer-performance";
    return <Navigate to={target} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Signed in", { description: "Welcome back." });
      // role-aware redirect — avoid extra hop through TechRouteGuard that caused first-render blank
      // user state is set inside login(); the Navigate above will fire on re-render.
    } else {
      toast.error("Sign in failed", { description: res.error });
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white" data-testid="login-page">
      <div className="hidden lg:flex relative bg-slate-900 text-white">
        <img
          src="https://images.pexels.com/photos/35290682/pexels-photo-35290682.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Technician at work"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3" data-testid="login-brand">
            <BrandLogo size={56} testId="login-brand-logo" />
            <div>
              <div className="font-heading font-bold text-lg">{COMPANY_NAME}</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-amber-200/90">Service Management</div>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-4xl font-bold leading-tight mb-3">
              Operate complaints,<br/>not spreadsheets.
            </h1>
            <p className="text-slate-300 max-w-md">
              The internal service operations console for HISENSE, HAVELLS, LLOYD, VOLTAS, AISEN, REO and ONEPLUS TV after-sales workflows.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 text-xs">
              <div className="border-t-2 border-white/40 pt-2">
                <div className="font-heading text-lg font-bold">14</div>
                <div className="uppercase tracking-wider text-slate-400">Status states</div>
              </div>
              <div className="border-t-2 border-white/40 pt-2">
                <div className="font-heading text-lg font-bold">7</div>
                <div className="uppercase tracking-wider text-slate-400">Brands</div>
              </div>
              <div className="border-t-2 border-white/40 pt-2">
                <div className="font-heading text-lg font-bold">6</div>
                <div className="uppercase tracking-wider text-slate-400">Roles</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            <BrandLogo size={36} testId="login-brand-logo-mobile" />
            <div className="font-heading font-bold">{SOFTWARE_NAME}</div>
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Use your service team credentials.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email or Phone</Label>
              <Input id="email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1" placeholder="email@example.com or 98xxxxxxxx" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="mt-1" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="login-submit-button">
              {submitting ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
